import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, ExternalLink, Building2, Loader2, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function AgentWebsite() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);

  useSEO({
    title: site?.page_title || `Agent — Krieger Real Estate`,
    description: site?.meta_description || 'Real estate agent at Krieger Real Estate.',
    canonical: site?.custom_domain ? `https://${site.custom_domain}` : `https://joinkrieger.com/agent/${slug}`,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', custom_fields: {} });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const isPreview = new URLSearchParams(window.location.search).has('preview');
    const url = isPreview
      ? `${API}/agent-site/${slug}/preview`
      : `${API}/agent-site/${slug}`;
    const opts = isPreview ? { withCredentials: true } : {};
    axios.get(url, opts)
      .then(r => setSite(r.data))
      .catch(() => setError('Agent website not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      await axios.post(`${API}/agent-site/${slug}/contact`, form);
      setSent(true);
    } catch { alert('Failed to submit. Please try again.'); }
    setSending(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 size={32} className="animate-spin text-slate-400" /></div>;
  if (error || !site) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center"><h1 className="text-2xl font-bold text-slate-800 mb-2">Page Not Found</h1><p className="text-slate-400">This agent website doesn't exist or isn't published yet.</p><a href="/" className="text-[#002FA7] text-sm underline mt-4 block">Go to Krieger Real Estate</a></div>
    </div>
  );

  const s = site.custom_style || {};
  const sections = (site.sections || []).filter(sec => sec.enabled);
  const r = s.border_radius || '12px';

  const renderSection = (section) => {
    switch (section.type) {
      case 'hero': {
        const c = section.content || {};
        return (
          <section key={section.id} className="relative min-h-[70vh] flex items-center" style={{ background: c.background_image ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.6)), url(${c.background_image}) center/cover` : s.accent || '#002FA7' }} data-testid="agent-hero">
            <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 w-full">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: s.font_heading || 'inherit' }}>{c.headline || site.agent_name}</h1>
                <p className="text-lg text-white/80 mb-8 max-w-xl" style={{ fontFamily: s.font_body || 'inherit' }}>{c.subheadline || 'Real Estate Agent'}</p>
                {c.cta_text && (
                  <a href="#contact" className="inline-flex items-center gap-2 text-sm font-medium px-6 py-3 text-white border-2 border-white/40 hover:bg-white/10 transition-colors" style={{ borderRadius: r }}>
                    {c.cta_text}
                  </a>
                )}
              </motion.div>
              <div className="mt-8 flex items-center gap-1">
                <span className="text-xs text-white/40">Powered by</span>
                <span className="text-xs text-white/60 font-semibold tracking-wider">KRIEGER REAL ESTATE</span>
              </div>
            </div>
          </section>
        );
      }
      case 'about': {
        const c = section.content || {};
        return (
          <section key={section.id} className="py-20" style={{ background: s.card_bg || '#F8FAFC' }} data-testid="agent-about">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {c.photo && (
                  <div className="md:col-span-1">
                    <img src={c.photo} alt={site.agent_name} className="w-full aspect-square object-cover" style={{ borderRadius: r }} />
                  </div>
                )}
                <div className={c.photo ? 'md:col-span-2' : 'md:col-span-3'}>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: s.text || '#0F172A', fontFamily: s.font_heading }}>About {site.agent_name}</h2>
                  <p className="leading-relaxed mb-6" style={{ color: (s.text || '#0F172A') + 'CC', fontFamily: s.font_body }}>{c.bio || 'A dedicated real estate professional committed to helping clients achieve their property goals.'}</p>
                  <div className="flex flex-wrap gap-3">
                    {c.years_experience && <span className="px-3 py-1.5 text-xs font-medium" style={{ background: (s.accent || '#002FA7') + '15', color: s.accent || '#002FA7', borderRadius: r }}>{c.years_experience} Years Experience</span>}
                    {c.specialization && <span className="px-3 py-1.5 text-xs font-medium capitalize" style={{ background: (s.accent || '#002FA7') + '15', color: s.accent || '#002FA7', borderRadius: r }}>{c.specialization.replace('_', ' ')}</span>}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      }
      case 'listings': {
        const listings = site.listings_data || [];
        if (listings.length === 0) return null;
        return (
          <section key={section.id} className="py-20" style={{ background: s.bg || '#FFFFFF' }} data-testid="agent-listings">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <h2 className="text-2xl font-bold mb-8" style={{ color: s.text || '#0F172A', fontFamily: s.font_heading }}>Active Listings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {listings.map((l, i) => (
                  <motion.div key={l.id || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="border overflow-hidden hover:shadow-lg transition-shadow" style={{ borderRadius: r, borderColor: s.text ? s.text + '15' : '#E2E8F0', background: s.card_bg || '#F8FAFC' }}>
                    {l.images?.[0]?.url && <img src={l.images[0].url} alt={l.title} className="w-full h-40 object-cover" />}
                    <div className="p-4">
                      <div className="text-lg font-bold" style={{ color: s.accent || '#002FA7' }}>{fmt(l.price)}</div>
                      <div className="text-sm font-medium mt-1" style={{ color: s.text || '#0F172A' }}>{l.title}</div>
                      <div className="text-xs mt-1" style={{ color: (s.text || '#0F172A') + '80' }}>{l.address}, {l.city}, {l.state}</div>
                      <div className="flex gap-2 mt-2 text-[10px]" style={{ color: (s.text || '#0F172A') + '60' }}>
                        {l.bedrooms && <span>{l.bedrooms} bd</span>}
                        {l.bathrooms && <span>{l.bathrooms} ba</span>}
                        {l.sqft && <span>{l.sqft.toLocaleString()} sqft</span>}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );
      }
      case 'testimonials': {
        const items = section.content?.items || [];
        if (items.length === 0) return null;
        return (
          <section key={section.id} className="py-20" style={{ background: s.card_bg || '#F8FAFC' }} data-testid="agent-testimonials">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <h2 className="text-2xl font-bold mb-8" style={{ color: s.text || '#0F172A', fontFamily: s.font_heading }}>Client Reviews</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((t, i) => (
                  <div key={t.id || i} className="p-6 border" style={{ borderRadius: r, borderColor: s.text ? s.text + '10' : '#E2E8F0', background: s.bg || '#FFFFFF' }}>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: (s.text || '#0F172A') + 'CC', fontFamily: s.font_body }}>"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: s.accent || '#002FA7' }}>{(t.name || '?')[0]}</div>
                      <div><div className="text-xs font-semibold" style={{ color: s.text || '#0F172A' }}>{t.name}</div>{t.title && <div className="text-[10px]" style={{ color: (s.text || '#0F172A') + '60' }}>{t.title}</div>}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      }
      case 'contact': {
        const c = section.content || {};
        const customFields = c.custom_fields || [];
        return (
          <section key={section.id} id="contact" className="py-20" style={{ background: s.bg || '#FFFFFF' }} data-testid="agent-contact">
            <div className="max-w-2xl mx-auto px-6 md:px-12">
              <h2 className="text-2xl font-bold mb-2" style={{ color: s.text || '#0F172A', fontFamily: s.font_heading }}>{c.heading || 'Get in Touch'}</h2>
              <p className="text-sm mb-8" style={{ color: (s.text || '#0F172A') + '80', fontFamily: s.font_body }}>{c.description || ''}</p>
              {sent ? (
                <div className="text-center py-10" style={{ color: s.accent || '#002FA7' }} data-testid="contact-sent">
                  <div className="text-3xl mb-2">✓</div>
                  <div className="text-lg font-bold">Message Sent!</div>
                  <p className="text-sm mt-1" style={{ color: (s.text || '#0F172A') + '80' }}>{site.agent_name} will be in touch shortly.</p>
                </div>
              ) : (
                <form onSubmit={submitContact} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your Name *" required className="px-4 py-3 border text-sm outline-none focus:ring-2" style={{ borderRadius: r, borderColor: s.text ? s.text + '20' : '#E2E8F0', color: s.text || '#0F172A', background: s.card_bg || '#F8FAFC' }} data-testid="contact-name" />
                    <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Your Email *" type="email" required className="px-4 py-3 border text-sm outline-none focus:ring-2" style={{ borderRadius: r, borderColor: s.text ? s.text + '20' : '#E2E8F0', color: s.text || '#0F172A', background: s.card_bg || '#F8FAFC' }} data-testid="contact-email" />
                  </div>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone (optional)" className="w-full px-4 py-3 border text-sm outline-none focus:ring-2" style={{ borderRadius: r, borderColor: s.text ? s.text + '20' : '#E2E8F0', color: s.text || '#0F172A', background: s.card_bg || '#F8FAFC' }} data-testid="contact-phone" />

                  {/* Custom Fields */}
                  {customFields.map((field, fi) => (
                    <div key={field.id || fi}>
                      {field.type === 'select' ? (
                        <select
                          value={form.custom_fields[field.label] || ''}
                          onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.label]: e.target.value } }))}
                          required={field.required}
                          className="w-full px-4 py-3 border text-sm outline-none focus:ring-2"
                          style={{ borderRadius: r, borderColor: s.text ? s.text + '20' : '#E2E8F0', color: form.custom_fields[field.label] ? (s.text || '#0F172A') : ((s.text || '#0F172A') + '60'), background: s.card_bg || '#F8FAFC' }}
                          data-testid={`custom-field-${fi}`}
                        >
                          <option value="">{field.label}{field.required ? ' *' : ''}</option>
                          {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : (
                        <input
                          value={form.custom_fields[field.label] || ''}
                          onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.label]: e.target.value } }))}
                          placeholder={`${field.label}${field.required ? ' *' : ''}`}
                          required={field.required}
                          className="w-full px-4 py-3 border text-sm outline-none focus:ring-2"
                          style={{ borderRadius: r, borderColor: s.text ? s.text + '20' : '#E2E8F0', color: s.text || '#0F172A', background: s.card_bg || '#F8FAFC' }}
                          data-testid={`custom-field-${fi}`}
                        />
                      )}
                    </div>
                  ))}

                  <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Message" rows={4} className="w-full px-4 py-3 border text-sm outline-none focus:ring-2 resize-none" style={{ borderRadius: r, borderColor: s.text ? s.text + '20' : '#E2E8F0', color: s.text || '#0F172A', background: s.card_bg || '#F8FAFC' }} data-testid="contact-message" />
                  <button type="submit" disabled={sending} className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium text-white transition-opacity" style={{ background: s.accent || '#002FA7', borderRadius: r, opacity: sending ? 0.6 : 1 }} data-testid="contact-submit">
                    <Send size={14} /> {sending ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </section>
        );
      }
      case 'social': {
        const links = section.content || {};
        const items = Object.entries(links).filter(([, v]) => v);
        if (items.length === 0) return null;
        return (
          <section key={section.id} className="py-12" style={{ background: s.card_bg || '#F8FAFC' }} data-testid="agent-social">
            <div className="max-w-5xl mx-auto px-6 md:px-12 flex flex-wrap justify-center gap-4">
              {items.map(([platform, url]) => (
                <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-xs font-medium border hover:shadow-md transition-shadow capitalize" style={{ borderRadius: r, color: s.accent || '#002FA7', borderColor: (s.accent || '#002FA7') + '30' }}>
                  {platform}
                </a>
              ))}
            </div>
          </section>
        );
      }
      default: return null;
    }
  };

  return (
    <div style={{ background: s.bg || '#FFFFFF', color: s.text || '#0F172A', fontFamily: s.font_body || 'Inter, sans-serif' }} data-testid="agent-website">
      {sections.map(renderSection)}
      {/* Footer */}
      <footer className="py-8 border-t text-center" style={{ borderColor: s.text ? s.text + '10' : '#E2E8F0' }}>
        <p className="text-[10px]" style={{ color: (s.text || '#0F172A') + '40' }}>
          {site.agent_name} — Krieger Real Estate · {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
}
